import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NO_VALUE_DASH, useFormats } from "@ryde/shared";

import { MAX_TRACING_ROWS } from "../investigation.constants";
import { IPlaybackModel } from "../investigation.models";
import {
  PanelMessageStyled,
  PanelSectionStyled,
  TracingRowStyled,
} from "../investigation.panel.styled";
import { buildTracingRows } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "../playback/use-playback-snapshot";

/** GPS tracing log — a decimated list of samples (time · speed · detour); click a row to seek. */
export const InvestigationTracing: FC<{ model: IPlaybackModel }> = ({
  model,
}) => {
  const { t } = useTranslation();
  const { formatTimeWithSecondsNormalized } = useFormats();
  const { seekToMs } = usePlaybackContext();
  const rows = useMemo(
    () => buildTracingRows(model, MAX_TRACING_ROWS),
    [model],
  );
  const snapshot = usePlaybackSnapshot();
  const currentMs = snapshot?.tMs;

  // The "current" sample is the last row the playhead has reached (rows are time-ordered).
  const activeIndex = useMemo(() => {
    if (currentMs === undefined) {
      return -1;
    }

    let index = -1;

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].tMs <= currentMs) {
        index = i;
      } else {
        break;
      }
    }

    return index;
  }, [rows, currentMs]);

  if (!rows.length) {
    return (
      <PanelMessageStyled>
        {t("rideMap.investigation.tracing.empty")}
      </PanelMessageStyled>
    );
  }

  return (
    <PanelSectionStyled>
      {rows.map((row, index) => (
        <TracingRowStyled
          key={`${row.tMs}-${index}`}
          $detour={row.inDetour}
          $active={index === activeIndex}
          onClick={() => seekToMs(row.tMs)}
        >
          <span className="tracing-time">
            {formatTimeWithSecondsNormalized(
              new Date(row.tMs).toISOString() as TDateISO,
            )}
          </span>
          <span className="tracing-speed">
            {row.speedKmh === undefined || row.speedKmh < 0
              ? NO_VALUE_DASH
              : t("rideMap.TRACKING_SPEED", { speed: row.speedKmh })}
          </span>
          <span className="tracing-detour">
            {row.inDetour ? t("rideMap.investigation.tracing.detour") : ""}
          </span>
        </TracingRowStyled>
      ))}
    </PanelSectionStyled>
  );
};
